import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SelectData } from "../../../models";
import {
  KanbanTask,
  KanbanTaskDrop,
  TasksDashboardFilterKey,
  TasksDashboardFilters,
  TasksDashboardProps,
  TasksDashboardView,
} from "./models";
import { applyFilters, buildFilterOptions, hasActiveFilters, moveTasks } from "./utils";

interface UseTasksDashboardStateResult {
  /**
   * True when the host owns filtering (controlled `filters`), so `tasks` is already filtered and any
   * server-side counts the host supplies still describe what is rendered.
   */
  isHostFiltered: boolean;
  /** Filter-applied tasks, before the hide toggles — the Kanban groups these into its lanes. */
  filteredTasks: KanbanTask[];
  view: TasksDashboardView;
  hideCompleted: boolean;
  hideUnassigned: boolean;
  /**
   * False while an assignee is selected, which hides the Unassigned lane on its own — the toggle then
   * has nothing left to decide, so the header drops it rather than offering a dead control.
   */
  canHideUnassigned: boolean;
  filters: TasksDashboardFilters;
  filterOptions: Record<TasksDashboardFilterKey, SelectData>;
  isEmpty: boolean;
  hasNoFilterResults: boolean;
  changeView: (view: TasksDashboardView) => void;
  toggleHideCompleted: (hide: boolean) => void;
  toggleHideUnassigned: (hide: boolean) => void;
  setFilter: (key: TasksDashboardFilterKey, values: string[]) => void;
  applyTaskDrop: (drop: KanbanTaskDrop) => void;
}

export const useTasksDashboardState = (props: TasksDashboardProps): UseTasksDashboardStateResult => {
  const {
    tasks,
    view: viewProp,
    onViewChange,
    hideCompleted: hideCompletedProp,
    onHideCompletedChange,
    hideUnassigned: hideUnassignedProp,
    onHideUnassignedChange,
    filters: filtersProp,
    onFilterChange,
    filterOptions: filterOptionsProp,
    onTaskMove,
    onTaskDrop,
    isLoading = false,
    ganttGroups = [],
    listGroups = [],
    isLoadingGroups = false,
    ganttViewMode,
    agendaTasks,
    isLoadingAgendaTasks = false,
  } = props;

  // Internal ordered task list — seeded from props and re-synced whenever the incoming list
  // changes. It is shared by all three views, which is why it lives here rather than on the board.
  const [orderedTasks, setOrderedTasks] = useState<KanbanTask[]>(tasks);
  useEffect(() => {
    setOrderedTasks(tasks);
  }, [tasks]);

  const [internalView, setInternalView] = useState<TasksDashboardView>(viewProp ?? "kanban");
  const view = viewProp ?? internalView;

  const [internalHideCompleted, setInternalHideCompleted] = useState<boolean>(hideCompletedProp ?? false);
  const hideCompleted = hideCompletedProp ?? internalHideCompleted;

  const [internalHideUnassigned, setInternalHideUnassigned] = useState<boolean>(hideUnassignedProp ?? false);
  const chosenHideUnassigned = hideUnassignedProp ?? internalHideUnassigned;

  const [internalFilters, setInternalFilters] = useState<TasksDashboardFilters>({});
  const filters = filtersProp ?? internalFilters;

  /*
    A task nobody owns can't match an assignee, so an assignee selection leaves the Unassigned lane
    permanently empty: it is hidden for as long as that selection stands, and the toggle that hides it
    goes with it — there is nothing left for it to decide. The user's own toggle choice is left
    untouched underneath, so clearing the assignee filter hands the lane back to it. A host filtering
    server-side derives the same thing for its queries, and so stops asking for the hidden lane.
  */
  const hasAssigneeFilter = Boolean(filters.assignee?.length);
  const canHideUnassigned = !hasAssigneeFilter;
  const hideUnassigned = hasAssigneeFilter || chosenHideUnassigned;

  /*
    Controlled filters mean the host filters server-side, so `tasks` already reflects them and the
    option keys come from the host (uuids, not display names). Filtering again here would double-apply
    the selection — and would contradict any server-side counts the host renders.
  */
  const isHostFiltered = filtersProp !== undefined;

  const derivedFilterOptions = useMemo(() => buildFilterOptions(orderedTasks), [orderedTasks]);
  const filterOptions = filterOptionsProp ?? derivedFilterOptions;

  const filteredTasks = useMemo(
    () => (isHostFiltered ? orderedTasks : applyFilters(orderedTasks, filters)),
    [isHostFiltered, orderedTasks, filters]
  );

  /*
    "No tasks yet" vs "No matching tasks" is decided by whether a filter is active: host-filtered
    tasks arrive already empty when nothing matches, so there is no unfiltered set to compare against.
    The hide toggles count as filters here — a host stops querying the lane they hide, so an
    all-completed organisation with "Hide completed" on returns nothing and would otherwise read as
    an account with no tasks at all.
  */
  const isFiltering = hasActiveFilters(filters) || hideCompleted || hideUnassigned;

  /*
    The grouped views (Gantt / List) are fed by their own group query, not by `tasks` — so their
    emptiness is the absence of *group rows*. Deriving it from the task list instead would let a
    grouped view read as "No tasks yet" while its own headers are on screen (and vice versa) whenever
    the two queries disagree, e.g. one of them failed or hasn't landed.
  */
  /*
    The Gantt's Agenda is fed by its own flat query rather than by the group headers, so its emptiness is
    the absence of *agenda rows*. Judging it by the headers would blank a populated agenda whenever the
    two requests disagree — a failed or empty group query would read as "No tasks yet" over rows that
    had loaded perfectly well.

    Two independent axes, and the row count only moves to `agendaTasks` when both hold. `isAgendaView`
    is which presentation is on screen; `isHostAgenda` is whether we own that flat query (same sense as
    `isHostFiltered` above). Without it the agenda is flattened out of the group rows instead, so the
    groups remain the thing to count — checking the view alone would read a loaded agenda as empty.
  */
  const isAgendaView = view === "gantt" && ganttViewMode === "agenda";
  const isHostAgenda = agendaTasks !== undefined;
  const isHostAgendaView = isAgendaView && isHostAgenda;
  const groupedView = view === "gantt" || view === "list";
  const groups = view === "gantt" ? ganttGroups : listGroups;

  let rowCount: number;
  let isRowsLoading: boolean;
  if (isHostAgendaView) {
    rowCount = (agendaTasks ?? []).length;
    isRowsLoading = isLoadingAgendaTasks;
  } else {
    rowCount = groups.length;
    isRowsLoading = isLoadingGroups;
  }

  const isViewLoading = isLoading || (groupedView && isRowsLoading && rowCount === 0);
  const unfilteredCount = groupedView ? rowCount : orderedTasks.length;
  const filteredCount = groupedView ? rowCount : filteredTasks.length;

  const isEmpty = !isViewLoading && unfilteredCount === 0 && !isFiltering;
  const hasNoFilterResults = !isViewLoading && filteredCount === 0 && isFiltering;

  const changeView = (next: TasksDashboardView): void => {
    if (viewProp === undefined) setInternalView(next);
    onViewChange?.(next);
  };

  const toggleHideCompleted = (hide: boolean): void => {
    if (hideCompletedProp === undefined) setInternalHideCompleted(hide);
    onHideCompletedChange?.(hide);
  };

  const toggleHideUnassigned = (hide: boolean): void => {
    if (hideUnassignedProp === undefined) setInternalHideUnassigned(hide);
    onHideUnassignedChange?.(hide);
  };

  const setFilter = (key: TasksDashboardFilterKey, values: string[]): void => {
    if (filtersProp === undefined) setInternalFilters((prev) => ({ ...prev, [key]: values }));
    onFilterChange?.(key, values);
  };

  // Refs so the (stable) drop handler can read the current list without re-binding on every change.
  const orderedTasksRef = useRef(orderedTasks);
  orderedTasksRef.current = orderedTasks;
  const onTaskMoveRef = useRef(onTaskMove);
  onTaskMoveRef.current = onTaskMove;
  const onTaskDropRef = useRef(onTaskDrop);
  onTaskDropRef.current = onTaskDrop;

  const applyTaskDrop = useCallback(({ taskIds, toStatus, beforeTaskId, toIndex }: KanbanTaskDrop): void => {
    setOrderedTasks((prev) => moveTasks(prev, taskIds, toStatus, beforeTaskId));
    // `moveTasks` inserts the block in board order, so report the move in that same order (not
    // the selection order) — otherwise a controlled parent persists a flipped order.
    const draggingSet = new Set(taskIds);
    const orderedIds = orderedTasksRef.current.filter((task) => draggingSet.has(task.id)).map((task) => task.id);

    // The whole block first, so a host that treats a multi-select drop as one action sees it before
    // the per-card calls — which have no boundary marking where one drop ends and the next begins.
    onTaskDropRef.current?.({ taskIds: orderedIds, toStatus, beforeTaskId, toIndex });
    orderedIds.forEach((taskId, i) => onTaskMoveRef.current?.(taskId, toStatus, toIndex + i));
  }, []);

  return {
    isHostFiltered,
    filteredTasks,
    view,
    hideCompleted,
    hideUnassigned,
    canHideUnassigned,
    filters,
    filterOptions,
    isEmpty,
    hasNoFilterResults,
    changeView,
    toggleHideCompleted,
    toggleHideUnassigned,
    setFilter,
    applyTaskDrop,
  };
};
